/**
 * LibraryPage - 案件资料库
 * 以案件为单位的文件夹管理系统
 * State is lifted to AppLayout; API calls persist to backend
 */

import React, { useState, useRef, useCallback } from 'react';
import {
    X, Upload, FileText, Film, Music, Image, File as FileIcon,
    Plus, Trash2, FolderOpen, ChevronRight, Search, ArrowLeft,
    Clock, Edit3, Folder,
} from 'lucide-react';
import { CaseFolder, CaseStatus, FileCategory, ResourceFile } from '../types/library';
import { ResourceApiService } from '../services/api';

// ═══════════════════════════════════════════
// Props
// ═══════════════════════════════════════════

interface LibraryPageProps {
    onClose: () => void;
    currentLanguage: string;
    cases: CaseFolder[];
    onCasesChange: (cases: CaseFolder[]) => void;
}

// ═══════════════════════════════════════════
// i18n Labels
// ═══════════════════════════════════════════

const labels = (isZh: boolean) => ({
    library: isZh ? '资料库' : 'Library',
    newCase: isZh ? '新建案件' : 'New Case',
    searchCases: isZh ? '搜索案件...' : 'Search cases...',
    close: isZh ? '关闭' : 'Close',
    back: isZh ? '返回' : 'Back',
    upload: isZh ? '上传文件' : 'Upload',
    delete: isZh ? '删除' : 'Delete',
    rename: isZh ? '重命名' : 'Rename',
    active: isZh ? '进行中' : 'Active',
    closed: isZh ? '已结案' : 'Closed',
    pending: isZh ? '待处理' : 'Pending',
    all: isZh ? '全部' : 'All',
    evidence: isZh ? '证据材料' : 'Evidence',
    correspondence: isZh ? '往来函件' : 'Correspondence',
    legal_docs: isZh ? '法律文书' : 'Legal Docs',
    notes: isZh ? '案件笔记' : 'Notes',
    other: isZh ? '其他' : 'Other',
    noCases: isZh ? '暂无案件' : 'No cases yet',
    noCasesDesc: isZh ? '点击「新建案件」开始管理您的案件资料' : 'Click "New Case" to start managing your case files',
    dragToUpload: isZh ? '拖拽文件到此处上传' : 'Drag & drop files here',
    supportedFormats: isZh ? '支持视频、音频、PDF、Word、文本、图片' : 'Video, audio, PDF, Word, text, images',
    browseFiles: isZh ? '选择文件' : 'Browse Files',
    dropToUpload: isZh ? '释放以上传文件' : 'Drop to upload',
    fileCount: (n: number) => isZh ? `${n} 个文件` : `${n} files`,
    untitledCase: isZh ? '未命名案件' : 'Untitled Case',
});

// ═══════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════

const getFileMediaType = (file: File): ResourceFile['type'] => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (['mp4', 'avi', 'mov', 'webm', 'mkv'].includes(ext)) return 'video';
    if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(ext)) return 'audio';
    if (['pdf', 'doc', 'docx', 'txt', 'md', 'rtf', 'xlsx', 'xls', 'pptx', 'ppt', 'csv'].includes(ext)) return 'document';
    if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext)) return 'image';
    return 'other';
};

const getTypeIcon = (type: ResourceFile['type'], size = 18) => {
    switch (type) {
        case 'video': return <Film size={size} className="text-purple-500" />;
        case 'audio': return <Music size={size} className="text-green-500" />;
        case 'document': return <FileText size={size} className="text-blue-500" />;
        case 'image': return <Image size={size} className="text-orange-500" />;
        default: return <FileIcon size={size} className="text-gray-400" />;
    }
};

const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const formatDate = (d: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return '刚刚';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour}h ago`;
    return d.toLocaleDateString();
};

const statusColors: Record<CaseStatus, { bg: string; text: string; dot: string }> = {
    active: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    closed: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
    pending: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
};

// ═══════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════

const LibraryPage: React.FC<LibraryPageProps> = ({ onClose, currentLanguage, cases, onCasesChange }) => {
    const isZh = currentLanguage.startsWith('zh');
    const t = labels(isZh);

    const [activeCaseId, setActiveCaseId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState<FileCategory>('all');
    const [isDragOver, setIsDragOver] = useState(false);
    const [editingCaseId, setEditingCaseId] = useState<string | null>(null);
    const [editingName, setEditingName] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const editInputRef = useRef<HTMLInputElement>(null);

    const activeCase = cases.find(c => c.id === activeCaseId) || null;

    // ─── Case CRUD (API-backed) ────────────
    const createCase = async () => {
        const name = t.untitledCase;
        try {
            const response = await ResourceApiService.createFolder(name);
            if (response.success && response.folder) {
                const newCase: CaseFolder = {
                    id: response.folder.id,
                    name: response.folder.name,
                    status: response.folder.status,
                    enabled: response.folder.enabled,
                    files: [],
                    createdAt: new Date(response.folder.createdAt),
                    updatedAt: new Date(response.folder.updatedAt),
                };
                onCasesChange([newCase, ...cases]);
                setEditingCaseId(newCase.id);
                setEditingName(newCase.name);
                setTimeout(() => editInputRef.current?.select(), 50);
            }
        } catch (error) {
            console.error('Failed to create folder:', error);
            // Fallback: create locally
            const newCase: CaseFolder = {
                id: crypto.randomUUID(),
                name,
                status: 'active',
                files: [],
                enabled: true,
                createdAt: new Date(),
                updatedAt: new Date(),
            };
            onCasesChange([newCase, ...cases]);
        }
    };

    const deleteCase = async (id: string) => {
        // Optimistic update
        onCasesChange(cases.filter(c => c.id !== id));
        if (activeCaseId === id) setActiveCaseId(null);
        try {
            await ResourceApiService.deleteFolder(id);
        } catch (error) {
            console.error('Failed to delete folder:', error);
        }
    };

    const confirmRename = async (id: string) => {
        const newName = editingName.trim() || t.untitledCase;
        onCasesChange(cases.map(c =>
            c.id === id ? { ...c, name: newName, updatedAt: new Date() } : c
        ));
        setEditingCaseId(null);
        try {
            await ResourceApiService.updateFolder(id, { name: newName });
        } catch (error) {
            console.error('Failed to rename folder:', error);
        }
    };

    const cycleStatus = async (id: string) => {
        const order: CaseStatus[] = ['active', 'pending', 'closed'];
        const current = cases.find(c => c.id === id);
        if (!current) return;
        const next = order[(order.indexOf(current.status) + 1) % order.length];
        onCasesChange(cases.map(c =>
            c.id === id ? { ...c, status: next, updatedAt: new Date() } : c
        ));
        try {
            await ResourceApiService.updateFolder(id, { status: next });
        } catch (error) {
            console.error('Failed to update status:', error);
        }
    };

    // ─── File Management (API-backed) ──────
    const addFilesToCase = useCallback(async (files: FileList | File[], category: FileCategory = 'other') => {
        if (!activeCaseId) return;
        setIsUploading(true);

        try {
            const fileArray = Array.from(files);
            const response = await ResourceApiService.uploadFiles(activeCaseId, fileArray, category);

            if (response.success && response.files) {
                const newFiles: ResourceFile[] = response.files.map((f: any) => ({
                    id: f.id,
                    name: f.name,
                    type: f.type as ResourceFile['type'],
                    category: f.category as FileCategory,
                    size: f.size,
                    uploadedAt: new Date(f.uploadedAt),
                }));
                onCasesChange(cases.map(c =>
                    c.id === activeCaseId
                        ? { ...c, files: [...c.files, ...newFiles], updatedAt: new Date() }
                        : c
                ));
            }
        } catch (error) {
            console.error('Failed to upload files:', error);
            // Fallback: add locally
            const newFiles: ResourceFile[] = Array.from(files).map(file => ({
                id: crypto.randomUUID(),
                name: (file as File).name,
                type: getFileMediaType(file as File),
                category,
                size: (file as File).size,
                uploadedAt: new Date(),
                file: file as File,
            }));
            onCasesChange(cases.map(c =>
                c.id === activeCaseId
                    ? { ...c, files: [...c.files, ...newFiles], updatedAt: new Date() }
                    : c
            ));
        } finally {
            setIsUploading(false);
        }
    }, [activeCaseId, cases, onCasesChange]);

    const deleteFile = async (fileId: string) => {
        if (!activeCaseId) return;
        // Optimistic update
        onCasesChange(cases.map(c =>
            c.id === activeCaseId
                ? { ...c, files: c.files.filter(f => f.id !== fileId), updatedAt: new Date() }
                : c
        ));
        try {
            await ResourceApiService.deleteFile(fileId);
        } catch (error) {
            console.error('Failed to delete file:', error);
        }
    };

    // ─── Drag & Drop ───────────────────────
    const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false);
    };
    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files.length > 0) addFilesToCase(e.dataTransfer.files, activeCategory === 'all' ? 'other' : activeCategory);
    };

    // ─── Filtered ──────────────────────────
    const filteredFiles = activeCase
        ? activeCase.files.filter(f => activeCategory === 'all' || f.category === activeCategory)
        : [];

    const filteredCases = cases.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const categories: { key: FileCategory; label: string }[] = [
        { key: 'all', label: t.all },
        { key: 'evidence', label: t.evidence },
        { key: 'correspondence', label: t.correspondence },
        { key: 'legal_docs', label: t.legal_docs },
        { key: 'notes', label: t.notes },
        { key: 'other', label: t.other },
    ];

    // ═══════════════════════════════════════════
    // RENDER: Case Detail (Level 2)
    // ═══════════════════════════════════════════
    if (activeCase) {
        return (
            <div className="h-screen flex flex-col bg-gray-50">
                <header className="flex items-center justify-between px-6 py-3 bg-white border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <button onClick={() => { setActiveCaseId(null); setActiveCategory('all'); }}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                            <ArrowLeft size={18} className="text-gray-500" />
                        </button>
                        <div className="flex items-center gap-1.5 text-sm">
                            <span className="text-gray-400 cursor-pointer hover:text-gray-600"
                                onClick={() => { setActiveCaseId(null); setActiveCategory('all'); }}>
                                {t.library}
                            </span>
                            <ChevronRight size={14} className="text-gray-300" />
                            <span className="font-medium text-gray-900">{activeCase.name}</span>
                        </div>
                        <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[activeCase.status].bg} ${statusColors[activeCase.status].text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${statusColors[activeCase.status].dot}`} />
                            {t[activeCase.status]}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => fileInputRef.current?.click()}
                            disabled={isUploading}
                            className={`flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors ${isUploading ? 'opacity-50 cursor-wait' : ''}`}>
                            <Upload size={14} /> {isUploading ? '...' : t.upload}
                        </button>
                        <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                            <X size={18} className="text-gray-400" />
                        </button>
                    </div>
                </header>

                <input ref={fileInputRef} type="file" multiple className="hidden"
                    accept="video/*,audio/*,.pdf,.doc,.docx,.txt,.md,.rtf,.xlsx,.xls,.pptx,.ppt,.csv,image/*"
                    onChange={(e) => { if (e.target.files) addFilesToCase(e.target.files, activeCategory === 'all' ? 'other' : activeCategory); e.target.value = ''; }} />

                {/* Category Tabs */}
                <div className="px-6 pt-3 pb-0 bg-white border-b border-gray-200">
                    <div className="flex gap-1 overflow-x-auto">
                        {categories.map(cat => (
                            <button key={cat.key} onClick={() => setActiveCategory(cat.key)}
                                className={`px-3 py-2 text-xs font-medium rounded-t-lg transition-colors whitespace-nowrap border-b-2
                                    ${activeCategory === cat.key
                                        ? 'border-gray-900 text-gray-900 bg-gray-50'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                    }`}>
                                {cat.label}
                                <span className="ml-1.5 text-gray-400">
                                    {cat.key === 'all' ? activeCase.files.length : activeCase.files.filter(f => f.category === cat.key).length}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* File Area */}
                <div className="flex-1 overflow-y-auto p-6" onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
                    {isDragOver && (
                        <div className="mb-4 p-6 border-2 border-dashed border-gray-900 rounded-xl bg-gray-100 text-center">
                            <Upload size={22} className="mx-auto mb-1 text-gray-900" />
                            <p className="text-sm font-medium text-gray-700">{t.dropToUpload}</p>
                        </div>
                    )}
                    {filteredFiles.length === 0 && !isDragOver ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                            <div className="p-3 bg-gray-100 rounded-full mb-3"><Upload size={24} className="text-gray-400" /></div>
                            <p className="text-sm text-gray-500 mb-1">{t.dragToUpload}</p>
                            <p className="text-xs text-gray-400 mb-4">{t.supportedFormats}</p>
                            <button onClick={() => fileInputRef.current?.click()}
                                className="px-4 py-2 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors">
                                {t.browseFiles}
                            </button>
                        </div>
                    ) : (
                        <div className="grid gap-2">
                            {filteredFiles.map(file => (
                                <div key={file.id} className="group flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
                                    {getTypeIcon(file.type)}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-900 truncate">{file.name}</div>
                                        <div className="text-xs text-gray-400 mt-0.5">{formatSize(file.size)} · {formatDate(file.uploadedAt)}</div>
                                    </div>
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                                        {categories.find(c => c.key === file.category)?.label}
                                    </span>
                                    <button onClick={() => deleteFile(file.id)}
                                        className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded-lg transition-all">
                                        <Trash2 size={14} className="text-gray-400 hover:text-red-500" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════
    // RENDER: Case List (Level 1)
    // ═══════════════════════════════════════════
    return (
        <div className="h-screen flex flex-col bg-gray-50">
            <header className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
                <div className="flex items-center gap-3">
                    <FolderOpen size={22} className="text-gray-700" />
                    <h1 className="text-lg font-semibold text-gray-900">{t.library}</h1>
                    {cases.length > 0 && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{cases.length}</span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={createCase}
                        className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors">
                        <Plus size={16} /> {t.newCase}
                    </button>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>
            </header>

            {cases.length > 0 && (
                <div className="px-6 py-3 bg-white border-b border-gray-100">
                    <div className="relative max-w-md">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                            placeholder={t.searchCases}
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:border-gray-300 transition-colors" />
                    </div>
                </div>
            )}

            <div className="flex-1 overflow-y-auto p-6">
                {filteredCases.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="p-4 bg-gray-100 rounded-2xl mb-4"><Folder size={36} className="text-gray-300" /></div>
                        <h3 className="text-base font-medium text-gray-600 mb-1">{t.noCases}</h3>
                        <p className="text-sm text-gray-400 mb-6 max-w-sm">{t.noCasesDesc}</p>
                        <button onClick={createCase}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors">
                            <Plus size={16} /> {t.newCase}
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {filteredCases.map(caseItem => (
                            <div key={caseItem.id}
                                className="group bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
                                onClick={() => { if (editingCaseId !== caseItem.id) setActiveCaseId(caseItem.id); }}>
                                <div className="p-4">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <Folder size={18} className="text-gray-400 flex-shrink-0" />
                                            {editingCaseId === caseItem.id ? (
                                                <input ref={editInputRef} value={editingName} onChange={e => setEditingName(e.target.value)}
                                                    onKeyDown={e => { if (e.key === 'Enter') confirmRename(caseItem.id); if (e.key === 'Escape') setEditingCaseId(null); }}
                                                    onBlur={() => confirmRename(caseItem.id)} onClick={e => e.stopPropagation()}
                                                    className="text-sm font-medium text-gray-900 bg-gray-50 border border-gray-300 rounded px-1.5 py-0.5 flex-1 min-w-0 focus:outline-none focus:border-gray-400"
                                                    autoFocus />
                                            ) : (
                                                <span className="text-sm font-medium text-gray-900 truncate">{caseItem.name}</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                                            <button onClick={(e) => { e.stopPropagation(); setEditingCaseId(caseItem.id); setEditingName(caseItem.name); setTimeout(() => editInputRef.current?.select(), 50); }}
                                                className="p-1 hover:bg-gray-100 rounded transition-colors" title={t.rename}>
                                                <Edit3 size={12} className="text-gray-400" />
                                            </button>
                                            <button onClick={(e) => { e.stopPropagation(); deleteCase(caseItem.id); }}
                                                className="p-1 hover:bg-red-50 rounded transition-colors" title={t.delete}>
                                                <Trash2 size={12} className="text-gray-400 hover:text-red-500" />
                                            </button>
                                        </div>
                                    </div>
                                    <button onClick={(e) => { e.stopPropagation(); cycleStatus(caseItem.id); }}
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mb-3 hover:opacity-80 transition-opacity ${statusColors[caseItem.status].bg} ${statusColors[caseItem.status].text}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${statusColors[caseItem.status].dot}`} />
                                        {t[caseItem.status]}
                                    </button>
                                    <div className="flex items-center justify-between text-xs text-gray-400">
                                        <span className="flex items-center gap-1"><FileText size={12} /> {t.fileCount(caseItem.files.length)}</span>
                                        <span className="flex items-center gap-1"><Clock size={12} /> {formatDate(caseItem.updatedAt)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LibraryPage;
